import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './IntegrationDetails.css';

const IntegrationDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [integration, setIntegration] = useState(null);
    const [forms, setForms] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [reminderStats, setReminderStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processResult, setProcessResult] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    console.log('IntegrationDetails component rendered with ID:', id);

    const fetchForms = useCallback(async () => {
        try {
            console.log(`Fetching forms for integration ${id}...`);
            const formsResponse = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${id}/forms`);
            console.log('Forms API response:', formsResponse);
            setForms(formsResponse.data);
            return formsResponse.data;
        } catch (err) {
            console.error('Error fetching forms:', err);
            console.error('Error details:', err.response?.data || err.message);
            setError('Failed to load forms. Please try again.');
            return [];
        }
    }, [id]);

    // Fetch analytics data
    const fetchAnalytics = useCallback(async () => {
        try {
            console.log(`Fetching analytics for integration ${id}...`);
            const analyticsResponse = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${id}/analytics`);
            console.log('Analytics API response:', analyticsResponse);
            setAnalytics(analyticsResponse.data);
            
            // Also fetch reminder stats
            const reminderResponse = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${id}/reminders`);
            console.log('Reminder stats API response:', reminderResponse);
            setReminderStats(reminderResponse.data);
            
        } catch (err) {
            console.error('Error fetching analytics:', err);
            // Don't set error state for analytics failure, just log it
        }
    }, [id]);

    useEffect(() => {
        const fetchIntegrationDetails = async () => {
            console.log('Starting to fetch integration details for ID:', id);
            try {
                setLoading(true);
                
                // Fetch integration details
                console.log(`Making request to: ${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${id}`);
                const integrationResponse = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${id}`);
                console.log('Integration API response:', integrationResponse);
                setIntegration(integrationResponse.data);
                
                // Fetch forms for this integration
                const formsData = await fetchForms();
                console.log(`Fetched ${formsData.length} forms for integration`);
                
                // Fetch analytics data
                await fetchAnalytics();
                
                setLoading(false);
            } catch (err) {
                console.error('Error fetching integration details:', err);
                console.error('Error details:', err.response?.data || err.message);
                setError('Failed to load integration details. Please try again.');
                setLoading(false);
            }
        };

        if (id) {
            console.log('ID is available, fetching details...');
            fetchIntegrationDetails();
        } else {
            console.error('No integration ID provided in URL');
            setError('No integration ID provided');
            setLoading(false);
        }
    }, [id, fetchAnalytics, fetchForms]);

    // Add debugging for render phase
    console.log('Integration details render state:', { 
        loading, 
        error, 
        hasIntegration: !!integration, 
        formCount: forms.length 
    });

    const handleRunManualCheck = async () => {
        setIsProcessing(true);
        setProcessResult(null);
        
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${id}/process`);
            
            setProcessResult({
                success: true,
                message: `Process completed successfully. ${response.data.formsCreated} form(s) created.`
            });
            
            // Refresh forms list after processing
            await fetchForms();
            
        } catch (error) {
            console.error('Error running manual check:', error);
            setProcessResult({
                success: false,
                message: error.response?.data?.error || 'An error occurred while processing the integration'
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const handleExportData = async () => {
        try {
            setIsExporting(true);
            console.log(`Exporting data for integration ${id}`);
            
            const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${id}/export`, {
                responseType: 'blob'
            });
            
            // Create blob and download
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Extract filename from response headers or create default
            const contentDisposition = response.headers['content-disposition'];
            let filename = `${integration?.companyName}_Report.xlsx`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            console.log('Excel export completed successfully');
            
        } catch (err) {
            console.error('Error exporting data:', err);
            alert('Failed to export data. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleProcessReminders = async () => {
        try {
            setIsProcessing(true);
            console.log(`Processing reminders for integration ${id}`);
            
            const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${id}/process-reminders`);
            console.log('Reminder processing response:', response.data);
            
            setProcessResult({
                success: true,
                message: `Reminders processed: ${response.data.sent} sent, ${response.data.errors} errors`
            });
            
            // Refresh analytics after processing
            await fetchAnalytics();
            
        } catch (err) {
            console.error('Error processing reminders:', err);
            setProcessResult({
                success: false,
                message: err.response?.data?.error || 'Failed to process reminders'
            });
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCheckCompletions = async () => {
        try {
            setIsProcessing(true);
            console.log(`Checking form completions for integration ${id}`);
            
            const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${id}/check-completions`);
            console.log('Form completion check response:', response.data);
            
            setProcessResult({
                success: true,
                message: `Form completions checked: ${response.data.processed} processed, ${response.data.completed} newly completed`
            });
            
            // Refresh analytics and forms after checking
            await fetchAnalytics();
            await fetchForms();
            
        } catch (err) {
            console.error('Error checking form completions:', err);
            setProcessResult({
                success: false,
                message: err.response?.data?.error || 'Failed to check form completions'
            });
        } finally {
            setIsProcessing(false);
        }
    };


    // Add a method to check form status
    const checkFormStatus = async () => {
        setIsProcessing(true);
        
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/employees/integration/${id}/check-status`);
            alert(response.data.message);
            
            // Refresh forms after checking status
            fetchForms();
            
        } catch (error) {
            console.error('Error checking form status:', error);
            alert('Error checking form status: ' + (error.response?.data?.error || error.message));
        } finally {
            setIsProcessing(false);
        }
    };

    if (loading) {
        console.log('Showing loading state');
        return <div className="loading">Loading integration details...</div>;
    }
    
    if (error) {
        console.log('Showing error state:', error);
        return <div className="error">{error}</div>;
    }
    
    if (!integration) {
        console.log('Integration not found');
        return <div className="error">Integration not found</div>;
    }

    console.log('Rendering integration details', integration);
    console.log('Forms available:', forms);

    return (
        <div className="integration-details">
            <h1>{integration.companyName} Integration</h1>
            
            <div className="integration-info">
                <p><strong>API Token:</strong> {integration.apiToken?.substring(0, 10)}...</p>
                <p><strong>Required Days:</strong> {integration.requiredDays}</p>
                <p><strong>Employee Form ID:</strong> {integration.employeeFormId}</p>
                <p><strong>Manager Form ID:</strong> {integration.managerFormId}</p>
                <p><strong>Created At:</strong> {integration.createdAt ? new Date(integration.createdAt).toLocaleString() : 'Unknown'}</p>
                <p><strong>S3 Location:</strong> {integration.folderPath}</p>
                <p><strong>Status:</strong> <span className={`status ${integration.status}`}>{integration.status}</span></p>
            </div>
            
            <div className="integration-actions mt-4 mb-4">
                <div className="action-buttons">
                    <button 
                        className="btn-primary" 
                        onClick={handleRunManualCheck} 
                        disabled={isProcessing}
                    >
                        {isProcessing ? 'Processing...' : 'Run Manual Check'}
                    </button>
                    
                    <button 
                        className="btn-secondary" 
                        onClick={checkFormStatus} 
                        disabled={isProcessing}
                    >
                        {isProcessing ? 'Checking Status...' : 'Check Form Status'}
                    </button>
                </div>
                
                {processResult && (
                    <div className={`alert ${processResult.success ? 'alert-success' : 'alert-danger'} mt-3`}>
                        {processResult.message}
                    </div>
                )}
            </div>
            
            <div className="form-urls-section">
                <h3>Form URLs Configuration</h3>
                {integration && integration.formUrls ? (
                    <div className="form-urls-info">
                        <h4>First Period Forms</h4>
                        <p><strong>Employee Form:</strong> {integration.formUrls.firstPeriodEmployee || 'Not configured'}</p>
                        <p><strong>Manager Form:</strong> {integration.formUrls.firstPeriodManager || 'Not configured'}</p>
                        
                        <h4>Second Period Forms</h4>
                        <p><strong>Employee Form:</strong> {integration.formUrls.secondPeriodEmployee || 'Not configured'}</p>
                        <p><strong>Manager Form:</strong> {integration.formUrls.secondPeriodManager || 'Not configured'}</p>
                        
                        <div className="info-message">
                            <p><strong>Note:</strong> Second period forms will be generated automatically when employees reach the second milestone day ({integration.requiredDays?.split(',')[1] || '60'}).</p>
                        </div>
                    </div>
                ) : (
                    <p>No form URLs configured</p>
                )}
                <button 
                    className="btn-edit"
                    onClick={() => {
                        // You can implement editing functionality here
                        alert('Form URL editing will be added in a future update');
                    }}
                >
                    Edit Form URLs
                </button>
            </div>

            {/* Analytics Dashboard */}
            {analytics && (
                <div className="analytics-section">
                    <h2>Analytics & Reporting</h2>
                    
                    <div className="analytics-grid">
                        {/* Forms Overview */}
                        <div className="analytics-card">
                            <h3>Forms Overview</h3>
                            <div className="stats-grid">
                                <div className="stat-item">
                                    <span className="stat-number">{analytics.forms.total}</span>
                                    <span className="stat-label">Total Forms</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-number">{analytics.forms.byStatus?.completed || 0}</span>
                                    <span className="stat-label">Completed</span>
                                </div>
                                <div className="stat-item">
                                    <span className="stat-number">{analytics.forms.byStatus?.pending || 0}</span>
                                    <span className="stat-label">Pending</span>
                                </div>
                            </div>
                        </div>

                        {/* Reminder Statistics */}
                        {reminderStats && (
                            <div className="analytics-card">
                                <h3>Email Reminders</h3>
                                <div className="stats-grid">
                                    <div className="stat-item">
                                        <span className="stat-number">{reminderStats.reminders?.first?.sent || 0}</span>
                                        <span className="stat-label">1st Reminders Sent</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-number">{reminderStats.reminders?.second?.sent || 0}</span>
                                        <span className="stat-label">2nd Reminders Sent</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-number">{reminderStats.reminders?.third?.sent || 0}</span>
                                        <span className="stat-label">3rd Reminders Sent</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Typeform Statistics */}
                        {analytics.typeformStats && Object.keys(analytics.typeformStats).length > 0 && (
                            <div className="analytics-card">
                                <h3>Typeform Responses</h3>
                                <div className="typeform-stats">
                                    {Object.entries(analytics.typeformStats).map(([formType, stats]) => (
                                        <div key={formType} className="typeform-stat-item">
                                            <span className="form-type">{formType}</span>
                                            <span className="response-count">{stats.completedResponses || 0} responses</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="analytics-actions">
                        <button 
                            className="btn btn-success"
                            onClick={handleProcessReminders}
                            disabled={isProcessing}
                        >
                            {isProcessing ? 'Processing...' : 'Process Reminders'}
                        </button>
                        
                        <button 
                            className="btn btn-info"
                            onClick={handleCheckCompletions}
                            disabled={isProcessing}
                        >
                            {isProcessing ? 'Checking...' : 'Check Form Completions'}
                        </button>
                        
                        <button 
                            className="btn btn-primary"
                            onClick={handleExportData}
                            disabled={isExporting}
                        >
                            {isExporting ? 'Exporting...' : '📊 Export Excel Report'}
                        </button>
                    </div>
                </div>
            )}
            
            <div className="forms-section">
                <h2>Generated Forms</h2>
                
                <div className="action-buttons">
                    <button 
                        className="primary-button"
                        onClick={() => navigate(`/integration/${id}/create-forms`)}
                    >
                        Create Employee Forms
                    </button>
                    <button 
                        className="secondary-button"
                        onClick={checkFormStatus}
                        disabled={isProcessing}
                    >
                        {isProcessing ? 'Checking...' : 'Check Form Status'}
                    </button>
                </div>
                
                {forms.length === 0 ? (
                    <p>No forms have been generated for this integration yet.</p>
                ) : (
                    <div className="forms-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Day</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {forms.map(form => (
                                    <tr key={form.id}>
                                        <td>{form.employeeName}</td>
                                        <td>{form.dayNumber}</td>
                                        <td>
                                            <div className="status-column">
                                                <span className={`status-badge ${form.status}`}>
                                                    {form.status === 'completed' ? 'Complete' : 'Pending'}
                                                </span>
                                                <div className="sub-statuses">
                                                    <span className={`status-badge small ${form.employeeFormStatus}`}>
                                                        {form.employeeFormStatus === 'completed' ? 'Employee ✓' : 'Employee ⏳'}
                                                    </span>
                                                    <span className={`status-badge small ${form.managerFormStatus}`}>
                                                        {form.managerFormStatus === 'completed' ? 'Manager ✓' : 'Manager ⏳'}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="form-actions">
                                                <div className="form-links">
                                                    <a href={form.employeeFormUrl} target="_blank" rel="noopener noreferrer">
                                                        Employee Form
                                                    </a>
                                                    {' | '}
                                                    <a href={form.managerFormUrl} target="_blank" rel="noopener noreferrer">
                                                        Manager Form
                                                    </a>
                                                </div>
                                                
                                                {/* PDF Download Links */}
                                                <div className="pdf-downloads">
                                                    {form.employeeFormPdfUrl && (
                                                        <a 
                                                            href={form.employeeFormPdfUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="pdf-button"
                                                        >
                                                            📄 Employee PDF
                                                        </a>
                                                    )}
                                                    
                                                    {form.managerFormPdfUrl && (
                                                        <a 
                                                            href={form.managerFormPdfUrl} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="pdf-button"
                                                        >
                                                            📄 Manager PDF
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IntegrationDetails;